import type { Locale } from '@/i18n';

/**
 * Locale-aware formatting for the Omani banking context (§1.2).
 *
 * Everything routes through `Intl`, which the browser implements against CLDR —
 * correct Arabic month names, correct Hijri conversion, correct plural rules —
 * rather than through a hand-rolled table that would drift.
 */

const OMR_DECIMALS = 3; // the rial is divided into 1000 baisa

/**
 * Should Arabic render Arabic-Indic digits (٠١٢) or Western ones (012)?
 *
 * Set from the `localisation.arabic_indic_digits` setting at boot. Defaults to
 * Western: Omani banking documents conventionally use Western digits even in
 * Arabic text, and account numbers and references are almost always read back
 * against a printed statement that uses them.
 */
let useArabicIndicDigits = false;

export function configureNumerals(enabled: boolean): void {
    useArabicIndicDigits = enabled;
}

/**
 * The BCP-47 tag to hand to Intl.
 *
 * `-u-nu-latn` forces Western digits inside an Arabic locale. Without it, Arabic
 * `Intl` output uses Arabic-Indic digits by default, which is not what most
 * Omani financial interfaces want.
 */
function intlLocale(locale: Locale): string {
    if (locale !== 'ar') {
        return 'en-GB';
    }

    return useArabicIndicDigits ? 'ar-OM' : 'ar-OM-u-nu-latn';
}

/* -------------------------------------------------------------------------- */
/* Numbers and currency                                                        */
/* -------------------------------------------------------------------------- */

export function formatNumber(
    value: number | null | undefined,
    locale: Locale,
    options: Intl.NumberFormatOptions = {},
): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return '—';
    }

    return new Intl.NumberFormat(intlLocale(locale), options).format(value);
}

/**
 * Omani rial, to three decimal places.
 *
 * Two decimals would silently drop baisa — a real rounding error on loan amounts,
 * not a cosmetic one.
 */
export function formatCurrency(
    value: number | null | undefined,
    locale: Locale,
    currency = 'OMR',
): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return '—';
    }

    return new Intl.NumberFormat(intlLocale(locale), {
        style: 'currency',
        currency,
        minimumFractionDigits: currency === 'OMR' ? OMR_DECIMALS : 2,
        maximumFractionDigits: currency === 'OMR' ? OMR_DECIMALS : 2,
    }).format(value);
}

export function formatPercent(
    value: number | null | undefined,
    locale: Locale,
    fractionDigits = 1,
): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return '—';
    }

    return new Intl.NumberFormat(intlLocale(locale), {
        style: 'percent',
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    }).format(value / 100);
}

/**
 * A compact duration, for wait times, handling times and SLA countdowns.
 *
 * Reads as "2h 15m" rather than "135 minutes" because that is how branch and
 * contact-centre staff talk about it, and because a wallboard has no room for
 * the long form.
 */
export function formatDuration(
    seconds: number | null | undefined,
    locale: Locale,
): string {
    if (seconds === null || seconds === undefined || Number.isNaN(seconds)) {
        return '—';
    }

    const isArabic = locale === 'ar';
    const abs = Math.abs(Math.round(seconds));

    const hours = Math.floor(abs / 3600);
    const minutes = Math.floor((abs % 3600) / 60);
    const remainder = abs % 60;

    const units = isArabic
        ? { h: 'س', m: 'د', s: 'ث' }
        : { h: 'h', m: 'm', s: 's' };

    if (hours > 0) {
        return `${hours}${units.h} ${minutes}${units.m}`;
    }

    if (minutes > 0) {
        return `${minutes}${units.m} ${remainder}${units.s}`;
    }

    return `${remainder}${units.s}`;
}

/* -------------------------------------------------------------------------- */
/* Dates                                                                       */
/* -------------------------------------------------------------------------- */

function toDate(value: string | Date | null | undefined): Date | null {
    if (value === null || value === undefined) {
        return null;
    }

    const date = value instanceof Date ? value : new Date(value);

    return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDate(
    value: string | Date | null | undefined,
    locale: Locale,
    options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
): string {
    const date = toDate(value);

    if (date === null) {
        return '—';
    }

    return new Intl.DateTimeFormat(intlLocale(locale), {
        timeZone: 'Asia/Muscat',
        ...options,
    }).format(date);
}

export function formatDateTime(
    value: string | Date | null | undefined,
    locale: Locale,
): string {
    return formatDate(value, locale, {
        dateStyle: 'medium',
        timeStyle: 'short',
    });
}

export function formatTime(
    value: string | Date | null | undefined,
    locale: Locale,
): string {
    return formatDate(value, locale, { timeStyle: 'short' });
}

/**
 * The Hijri (Islamic) date, shown alongside the Gregorian one when enabled.
 *
 * Uses the Umm al-Qura calendar, which is the civil Hijri calendar used across
 * the Gulf — `islamic` alone gives the tabular variant and can differ by a day
 * from the date on an Omani government document.
 */
export function formatHijri(
    value: string | Date | null | undefined,
    locale: Locale,
): string {
    const date = toDate(value);

    if (date === null) {
        return '—';
    }

    try {
        return new Intl.DateTimeFormat(
            `${locale === 'ar' ? 'ar-SA' : 'en'}-u-ca-islamic-umalqura`,
            {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
                timeZone: 'Asia/Muscat',
            },
        ).format(date);
    } catch {
        // Very old browsers may not carry the Umm al-Qura calendar data. Falling
        // back to the Gregorian rendering is better than showing an error beside
        // an otherwise fine date.
        return formatDate(value, locale);
    }
}

/**
 * A relative time such as "3 hours ago", localised.
 *
 * Chosen units mirror how staff read a case list: anything under a minute is
 * "just now", and anything past a month gets an absolute date instead, because
 * "4 months ago" is not precise enough to act on.
 */
export function formatRelative(
    value: string | Date | null | undefined,
    locale: Locale,
): string {
    const date = toDate(value);

    if (date === null) {
        return '—';
    }

    const seconds = Math.round((date.getTime() - Date.now()) / 1000);
    const absolute = Math.abs(seconds);

    if (absolute < 45) {
        return locale === 'ar' ? 'الآن' : 'just now';
    }

    if (absolute > 60 * 60 * 24 * 30) {
        return formatDate(value, locale);
    }

    const formatter = new Intl.RelativeTimeFormat(intlLocale(locale), {
        numeric: 'auto',
    });

    // Largest unit whose length still divides the elapsed time to a value of at
    // least one — so 90 minutes reads as "an hour ago", not "90 minutes ago".
    const units: [Intl.RelativeTimeFormatUnit, number][] = [
        ['week', 604800],
        ['day', 86400],
        ['hour', 3600],
        ['minute', 60],
        ['second', 1],
    ];

    const [unit, length] = units.find(([, size]) => absolute >= size) ?? [
        'second',
        1,
    ];

    return formatter.format(Math.round(seconds / length), unit);
}

/* -------------------------------------------------------------------------- */
/* Omani specifics                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Format an Omani phone number as `+968 9123 4567`.
 *
 * Wrapped so it always renders left-to-right, even inside Arabic text — a phone
 * number whose country code migrates to the wrong end is the single most common
 * bidi bug in Arabic interfaces.
 */
export function formatPhone(value: string | null | undefined): string {
    if (!value) {
        return '—';
    }

    const digits = value.replace(/\D/g, '');
    const local = digits.startsWith('968') ? digits.slice(3) : digits;

    if (local.length !== 8) {
        return value;
    }

    return `+968 ${local.slice(0, 4)} ${local.slice(4)}`;
}

/** Is this a valid Omani mobile or landline number? */
export function isValidOmaniPhone(value: string): boolean {
    const digits = value.replace(/\D/g, '');
    const local = digits.startsWith('968') ? digits.slice(3) : digits;

    return /^[792]\d{7}$/.test(local);
}

/** An 8-digit Omani civil ID. */
export function isValidCivilId(value: string): boolean {
    return /^\d{8}$/.test(value.replace(/\D/g, ''));
}

/**
 * A file size in the reader's language.
 */
export function formatFileSize(bytes: number, locale: Locale): string {
    const units =
        locale === 'ar'
            ? ['بايت', 'ك.بايت', 'م.بايت', 'ج.بايت']
            : ['B', 'KB', 'MB', 'GB'];

    let size = bytes;
    let unit = 0;

    while (size >= 1024 && unit < units.length - 1) {
        size /= 1024;
        unit += 1;
    }

    return `${formatNumber(size, locale, { maximumFractionDigits: unit === 0 ? 0 : 1 })} ${units[unit]}`;
}

/**
 * Text that must stay left-to-right inside an RTL paragraph.
 *
 * Wraps the value in Unicode isolate marks so a reference such as
 * `CMP-2026-000842` does not have its segments reordered by the bidi algorithm.
 * Prefer the `.ltr-nums` CSS class where you control the element; use this when
 * the value is embedded mid-sentence.
 */
export function isolateLtr(value: string): string {
    return `⁦${value}⁩`;
}
